/*
 * A saved view's query and the page's state, each made from the other (master-inbox plan, step
 * 3b). Saving writes the box and label the list is on, its search (tokens and words as one `q`),
 * its filters and its order; opening a view loads the same back into the search field, the chips
 * and the view menu, so what a view keeps is always what the rep can see and change.
 *
 * Views saved before Campaign and Files became search operators keep `campaign` and `files`; they
 * open as the tokens those chips now write.
 */
import type { InboxListQuery } from "../../src/inbox-v2/contract.ts";
import type { ViewQuery } from "./api.ts";
import { LABEL_NAMES } from "./labels.ts";
import type { ListFilters, ListSort } from "./list-prefs.ts";
import { HAS_FILES, type SearchToken, tokenText } from "./search-query.ts";

/** What the list on screen would be saved as. `base` is its box, label or ready view. */
export function viewQueryOf(
  base: Partial<InboxListQuery>,
  q: string,
  filters: ListFilters,
  sort: ListSort,
): ViewQuery {
  // Follow-up due and Meeting requests are places, never stored statuses, from the list's place or
  // its Status filter: an older build would refuse the views (LEA-486, LEA-494).
  const chosen = filters.status ?? base.status;
  const status = chosen === "follow_up" || chosen === "meeting" ? undefined : chosen;
  const label = base.label ?? filters.label;
  return {
    box: base.box ?? "inbox",
    ...(label ? { label } : {}),
    ...(status ? { status } : {}),
    sort,
    ...(q ? { q } : {}),
    ...(filters.unread ? { unread: "1" as const } : {}),
    ...(filters.days ? { days: filters.days } : {}),
    ...(filters.check ? { check: "1" as const } : {}),
  };
}

/** A stored view's filters, as the chips draw them. A view never keeps a reply agent status. */
export function filtersOf(query: Partial<InboxListQuery>): ListFilters {
  return {
    // A view never stores Follow-up due or Meeting requests (`viewQueryOf`); one that somehow did
    // narrows by nothing.
    status:
      query.status === "follow_up" || query.status === "meeting" ? null : (query.status ?? null),
    agent: null,
    unread: query.unread === "1",
    days: query.days ?? null,
    check: query.check === "1",
    label: query.label ?? null,
  };
}

/** The tokens a view saved before Campaign and Files were operators stands for. */
export function legacyTokens(query: Partial<InboxListQuery>): SearchToken[] {
  return [
    ...(query.campaign ? [{ kind: "campaign-id" as const, value: query.campaign }] : []),
    ...(query.files === "1" ? [HAS_FILES] : []),
  ];
}

const STATUS = {
  meeting: "Meeting requests",
  needs_reply: "Needs reply",
  waiting: "Waiting on lead",
} as const;

/** A name to start from: what narrows the list, in the words the page shows for it. */
export function suggestedName(
  tokens: readonly SearchToken[],
  words: string,
  filters: ListFilters,
  campaigns: readonly { id: string; name: string }[],
  fallback: string,
): string {
  const parts = [
    ...tokens.map((token) => {
      const text = tokenText(token, campaigns);
      return token.kind === "has" ? "With files" : text.value;
    }),
    ...(words.trim() ? [`“${words.trim()}”`] : []),
    ...(filters.status ? [STATUS[filters.status]] : []),
    ...(filters.unread ? ["Unseen"] : []),
    ...(filters.days ? [`Last ${filters.days} days`] : []),
    ...(filters.check ? ["Unsure label"] : []),
    ...(filters.label ? [filters.label === "none" ? "No label" : LABEL_NAMES[filters.label]] : []),
  ];
  return parts.length ? parts.join(", ") : fallback;
}
